import React, { useEffect, useRef, useState } from "react";
import {
  VStack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  FormErrorMessage,
  FormHelperText,
  Select,
  Stack,
  Button,
  Box,
  Image,
  HStack,
  Heading,
  Text,
  Flex,
  Alert,
  AlertIcon,
  AlertDescription,
  UnorderedList,
  ListItem,
  useBreakpointValue,
  useToast,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import { FaStore } from "react-icons/fa";
import { Field, Form, useFormikContext } from "formik";
import FormStepper from "./FormStepper";
import PhoneNumberInput from "./PhoneNumberInput";
import { businessCategories } from "../constants/businessCategories";
import BusinessCard from "./BusinessCard";
import LogoCropperModal from "./LogoCropperModal";
import PexelsSearchModal from "./PexelsSearchModal";
import { useLanguage } from "../contexts/LanguageContext";
import { getCategoryImage } from "../utils/categoryImage";
import { pexelsEnabled } from "../config";

export const steps = [
  { title: "businessForm.steps.contact" },
  { title: "businessForm.steps.businessInfo" },
  { title: "businessForm.steps.coupon" },
  { title: "businessForm.steps.preview" },
];

const BusinessFormContent = ({
  activeStep,
  setActiveStep,
  handleLogoChange,
  logoPreview,
  setLogoPreview,
  businessId,
  isEditMode,
  // When true, render all sections at once with a single Submit (the layout
  // used for edit and for the platform-side "Enter Details Now" add flow).
  // Defaults to isEditMode so external/claim consumers stay on the stepper.
  singlePage = isEditMode,
  isSubmitting,
  showAccountFields = false,
  accountMode = "create",
  onChangeAccountMode,
  supportedLanguages = ["en", "fr"],
}) => {
  const { language, setLanguage, translate } = useLanguage();
  const { values, setFieldValue } = useFormikContext();
  const showFrench = supportedLanguages.includes("fr");
  const fallbackIconSize = useBreakpointValue({ base: "40px", md: "60px" });

  // Stable per-session preview ID so the category fallback works identically
  // in add mode (no doc yet) and edit mode (real businessId). In edit, the
  // hash uses the real id so the preview matches what customers see; in add
  // the image is illustrative — when the doc is created its Firestore id will
  // pick its own (possibly different) image from the category pool.
  const previewIdRef = useRef(
    `preview-${Math.random().toString(36).slice(2, 12)}`
  );
  const effectivePreviewId = businessId || previewIdRef.current;

  // Mirrors the resolution rule used by BusinessCard on the purchase page:
  // prefer the uploaded logo, then a deterministic category fallback, then
  // the storefront icon.
  const fallbackCategoryImage = !logoPreview
    ? getCategoryImage(values.category, effectivePreviewId)
    : null;
  const resolvedLogoImage = logoPreview || fallbackCategoryImage;

  // When the parent flips us into "claim" mode after a 409, jump back to the
  // contact step so the user can see the inline password field rather than
  // submitting blindly from the preview step.
  useEffect(() => {
    if (showAccountFields && accountMode === "claim") {
      setActiveStep(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountMode]);

  // Cropper state: a freshly-picked file becomes a blob URL we feed to the
  // modal; on apply, the cropped blob is dispatched back through the original
  // handleLogoChange via a synthetic event so the parent's logo + preview
  // state stays the single source of truth.
  const [cropSourceUrl, setCropSourceUrl] = useState(null);
  const [isCropperOpen, setIsCropperOpen] = useState(false);
  const [isPexelsOpen, setIsPexelsOpen] = useState(false);
  const fileInputRef = useRef(null);
  const toast = useToast();

  const openCropperFor = (file) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      setCropSourceUrl(reader.result);
      setIsCropperOpen(true);
    };
    reader.readAsDataURL(file);
  };

  const onFilePicked = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    openCropperFor(file);
    // Reset the input so picking the same file again still fires onChange
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const onCropApplied = (blob, dataUrl) => {
    const file = new File([blob], "logo.jpg", { type: "image/jpeg" });
    const syntheticEvent = { target: { files: [file] } };
    handleLogoChange(syntheticEvent);
    setIsCropperOpen(false);
    setCropSourceUrl(null);
  };

  const onCropCancel = () => {
    setIsCropperOpen(false);
    setCropSourceUrl(null);
  };

  const reopenCropperFromPreview = () => {
    // Only works for local data URLs / blob URLs; remote URLs (existing
    // storage logos) would taint the canvas, so we skip re-cropping those.
    if (!logoPreview || /^https?:/.test(logoPreview)) return;
    setCropSourceUrl(logoPreview);
    setIsCropperOpen(true);
  };

  // Pull the selected Pexels photo down as a blob, wrap it in a File so the
  // rest of the pipeline can't tell it from a normal upload, then hand off
  // to the cropper. The data URL the cropper renders is same-origin, so the
  // canvas tainting issue we have with remote storage URLs doesn't apply.
  const openPexelsPicker = () => {
    if (!pexelsEnabled) {
      toast({
        title: "Coming soon",
        description:
          "The Pexels image library will be available in an upcoming release.",
        status: "info",
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    setIsPexelsOpen(true);
  };

  const handlePexelsPick = async (photo) => {
    try {
      const response = await fetch(photo.fullUrl);
      if (!response.ok) {
        throw new Error(`Pexels image fetch failed (${response.status})`);
      }
      const blob = await response.blob();
      const file = new File([blob], `pexels-${photo.id}.jpg`, {
        type: blob.type || "image/jpeg",
      });
      setIsPexelsOpen(false);
      openCropperFor(file);
    } catch (err) {
      console.error("Failed to load Pexels image", err);
      toast({
        title: "Could not load image",
        description: "Please try a different photo.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const canRecropExistingPreview =
    logoPreview && !/^https?:/.test(logoPreview);

  const handleCategoryChange = (selectedEnglishCategory) => {
    const selectedCategory = businessCategories.find(
      (category) => category.en === selectedEnglishCategory
    );

    if (selectedCategory) {
      setFieldValue("category", {
        en: selectedCategory.en,
        fr: selectedCategory.fr,
      });
    }
  };

  const PreviewStep = () => {
    const previewData = {
      id: effectivePreviewId,
      name: values.name[language] || values.name.en,
      category: values.category[language] || values.category.en,
      categoryKey: values.category,
      imageUrl: logoPreview,
      description:
        values.description?.[language] || values.description?.en || "",
      websiteUrl: values.websiteUrl,
      googleProfileUrl: values.googleProfileUrl,
      coupon: {
        title: values.offerTitle[language] || values.offerTitle.en,
        description:
          values.offerDescription[language] || values.offerDescription.en,
        termsAndExclusions:
          values.termsAndExclusions?.[language] ||
          values.termsAndExclusions?.en,
        maxValue: values.maxValue,
      },
    };

    const translations = {
      language,
      visitWebsite: translate("businessList.website"),
      googleProfile: translate("businessList.googleProfile"),
      termsAndExclusions: translate("businessList.termsAndExclusions"),
      maxValue: translate("businessList.maxValue"),
    };

    return (
      <VStack spacing={6} width="100%" align="stretch">
        {/* <Text fontSize="md" fontWeight="medium">
          {translate("businessForm.preview.description")}
        </Text> */}
        <Flex align="center" justify="center">
          <Box
            as="button"
            py={2}
            px={2}
            onClick={() => setLanguage("en")}
            color={language === "en" ? "black" : "gray.500"}
            fontWeight={language === "en" ? "bold" : "normal"}
          >
            EN
          </Box>
          <Box px={0.5} color="gray.300">
            |
          </Box>
          <Box
            as="button"
            py={2}
            px={2}
            onClick={() => setLanguage("fr")}
            color={language === "fr" ? "black" : "gray.500"}
            fontWeight={language === "fr" ? "bold" : "normal"}
          >
            FR
          </Box>
        </Flex>

        <Box maxW="500px" mx="auto">
          <BusinessCard business={previewData} translations={translations} />
        </Box>
      </VStack>
    );
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0: // Contact
        return (
          <VStack spacing={6} width="100%">
            <Text fontSize="md" fontWeight="medium" mb={4}>
              {translate("businessForm.contact.description")}
            </Text>

            <Field name="contactName">
              {({ field, form }) => (
                <FormControl
                  isInvalid={
                    form.errors.contactName && form.touched.contactName
                  }
                  isRequired
                >
                  <FormLabel fontSize="sm">
                    {translate("businessForm.contact.contactName.label")}
                  </FormLabel>
                  <Input {...field} />
                  <FormErrorMessage>{form.errors.contactName}</FormErrorMessage>
                </FormControl>
              )}
            </Field>

            <Field name="email">
              {({ field, form }) => (
                <FormControl
                  isInvalid={form.errors.email && form.touched.email}
                  isRequired
                >
                  <FormLabel fontSize="sm">
                    {translate("businessForm.contact.email.label")}
                  </FormLabel>
                  <Input {...field} type="email" />
                  <FormErrorMessage>{form.errors.email}</FormErrorMessage>
                </FormControl>
              )}
            </Field>

            {showAccountFields && (
              <>
                {accountMode === "claim" && (
                  <Alert status="info" borderRadius="md">
                    <AlertIcon />
                    <AlertDescription fontSize="sm">
                      This email already has an account. Enter your existing
                      password below to claim this coupon.{" "}
                      {onChangeAccountMode && (
                        <Button
                          variant="link"
                          size="sm"
                          onClick={() => onChangeAccountMode("create")}
                        >
                          Wrong email?
                        </Button>
                      )}
                    </AlertDescription>
                  </Alert>
                )}
                <Field name="password">
                  {({ field, form }) => (
                    <FormControl
                      isInvalid={form.errors.password && form.touched.password}
                      isRequired
                    >
                      <FormLabel fontSize="sm">
                        {accountMode === "claim"
                          ? "Your password"
                          : "Create a password"}
                      </FormLabel>
                      <Input
                        {...field}
                        type="password"
                        autoComplete={
                          accountMode === "claim"
                            ? "current-password"
                            : "new-password"
                        }
                      />
                      <FormErrorMessage>
                        {form.errors.password}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                </Field>
                {accountMode === "create" && (
                  <Field name="confirmPassword">
                    {({ field, form }) => (
                      <FormControl
                        isInvalid={
                          form.errors.confirmPassword &&
                          form.touched.confirmPassword
                        }
                        isRequired
                      >
                        <FormLabel fontSize="sm">Confirm password</FormLabel>
                        <Input
                          {...field}
                          type="password"
                          autoComplete="new-password"
                        />
                        <FormErrorMessage>
                          {form.errors.confirmPassword}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                )}
              </>
            )}

            <Field name="phoneNumber">
              {({ field, form }) => (
                <FormControl
                  isInvalid={
                    form.errors.phoneNumber && form.touched.phoneNumber
                  }
                  isRequired
                >
                  <FormLabel fontSize="sm">
                    {translate("businessForm.contact.phoneNumber.label")}
                  </FormLabel>
                  <PhoneNumberInput field={field} />
                  <FormErrorMessage>{form.errors.phoneNumber}</FormErrorMessage>
                </FormControl>
              )}
            </Field>
          </VStack>
        );

      case 1: // Business Info
        return (
          <VStack spacing={6} width="100%">
            <Stack
              direction={{ base: "column", md: "row" }}
              spacing={6}
              width="100%"
            >
              <Field name="name.en">
                {({ field, form }) => (
                  <FormControl
                    isInvalid={form.errors.name?.en && form.touched.name?.en}
                    isRequired
                  >
                    <FormLabel fontSize="sm">
                      {translate(
                        showFrench
                          ? "businessForm.businessInfo.businessName.english.label"
                          : "businessForm.businessInfo.businessName.label"
                      )}
                    </FormLabel>
                    <Input {...field} />
                    <FormErrorMessage>{form.errors.name?.en}</FormErrorMessage>
                  </FormControl>
                )}
              </Field>
              {showFrench && (
                <Field name="name.fr">
                  {({ field, form }) => (
                    <FormControl
                      isInvalid={form.errors.name?.fr && form.touched.name?.fr}
                    >
                      <FormLabel fontSize="sm">
                        {translate(
                          "businessForm.businessInfo.businessName.french.label"
                        )}
                      </FormLabel>
                      <Input {...field} />
                      <FormErrorMessage>{form.errors.name?.fr}</FormErrorMessage>
                    </FormControl>
                  )}
                </Field>
              )}
            </Stack>

            <Field name="category.en">
              {({ field, form }) => (
                <FormControl
                  isInvalid={
                    form.errors.category?.en && form.touched.category?.en
                  }
                  isRequired
                >
                  <FormLabel fontSize="sm">
                    {translate("businessForm.businessInfo.category.label")}
                  </FormLabel>
                  <Select
                    {...field}
                    placeholder={translate(
                      "businessForm.businessInfo.category.placeholder"
                    )}
                    onChange={(e) => {
                      handleCategoryChange(e.target.value);
                    }}
                    value={values.category.en || ""}
                  >
                    {businessCategories.map((category) => (
                      <option key={category.en} value={category.en}>
                        {`${category.en} / ${category.fr}`}
                      </option>
                    ))}
                  </Select>
                  <FormErrorMessage>
                    {form.errors.category?.en}
                  </FormErrorMessage>
                </FormControl>
              )}
            </Field>

            <Stack
              direction={{ base: "column", md: "row" }}
              spacing={6}
              width="100%"
            >
              <Field name="websiteUrl">
                {({ field, form }) => (
                  <FormControl
                    isInvalid={
                      form.errors.websiteUrl && form.touched.websiteUrl
                    }
                  >
                    <FormLabel fontSize="sm">
                      {translate("businessForm.businessInfo.websiteUrl.label")}
                    </FormLabel>
                    <Input {...field} type="url" />
                    <FormHelperText fontSize="xs">
                      Enter the full URL, including http:// or https://.
                    </FormHelperText>
                    <FormErrorMessage>
                      {form.errors.websiteUrl}
                    </FormErrorMessage>
                  </FormControl>
                )}
              </Field>
              <Field name="googleProfileUrl">
                {({ field, form }) => (
                  <FormControl
                    isInvalid={
                      form.errors.googleProfileUrl &&
                      form.touched.googleProfileUrl
                    }
                  >
                    <FormLabel fontSize="sm">
                      {translate(
                        "businessForm.businessInfo.googleProfileUrl.label"
                      )}
                    </FormLabel>
                    <Input {...field} type="url" />
                    <FormHelperText fontSize="xs">
                      Enter the full URL, including http:// or https://.
                    </FormHelperText>
                    <FormErrorMessage>
                      {form.errors.googleProfileUrl}
                    </FormErrorMessage>
                  </FormControl>
                )}
              </Field>
            </Stack>
          </VStack>
        );

      case 2: // Coupon
        return (
          <VStack spacing={6} width="100%">
            <Stack
              direction={{ base: "column", md: "row" }}
              spacing={6}
              width="100%"
            >
              <Field name="offerTitle.en">
                {({ field, form }) => (
                  <FormControl
                    isInvalid={
                      form.errors.offerTitle?.en && form.touched.offerTitle?.en
                    }
                    isRequired
                  >
                    <FormLabel fontSize="sm">
                      {translate(
                        showFrench
                          ? "businessForm.coupon.offerTitle.english.label"
                          : "businessForm.coupon.offerTitle.label"
                      )}
                    </FormLabel>
                    <Input
                      {...field}
                      placeholder={translate(
                        "businessForm.coupon.offerTitle.english.placeholder"
                      )}
                    />
                    <FormErrorMessage>
                      {form.errors.offerTitle?.en}
                    </FormErrorMessage>
                  </FormControl>
                )}
              </Field>
              {showFrench && (
                <Field name="offerTitle.fr">
                  {({ field, form }) => (
                    <FormControl
                      isInvalid={
                        form.errors.offerTitle?.fr && form.touched.offerTitle?.fr
                      }
                    >
                      <FormLabel fontSize="sm">
                        {translate("businessForm.coupon.offerTitle.french.label")}
                      </FormLabel>
                      <Input
                        {...field}
                        placeholder={translate(
                          "businessForm.coupon.offerTitle.french.placeholder"
                        )}
                      />
                      <FormErrorMessage>
                        {form.errors.offerTitle?.fr}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                </Field>
              )}
            </Stack>

            <Stack
              direction={{ base: "column", md: "row" }}
              spacing={6}
              width="100%"
            >
              <Field name="offerDescription.en">
                {({ field, form }) => (
                  <FormControl
                    isInvalid={
                      form.errors.offerDescription?.en &&
                      form.touched.offerDescription?.en
                    }
                    isRequired
                  >
                    <FormLabel fontSize="sm">
                      {translate(
                        showFrench
                          ? "businessForm.coupon.offerDescription.english.label"
                          : "businessForm.coupon.offerDescription.label"
                      )}
                    </FormLabel>
                    <Textarea
                      {...field}
                      placeholder={translate(
                        "businessForm.coupon.offerDescription.english.placeholder"
                      )}
                    />
                    <FormErrorMessage>
                      {form.errors.offerDescription?.en}
                    </FormErrorMessage>
                  </FormControl>
                )}
              </Field>
              {showFrench && (
                <Field name="offerDescription.fr">
                  {({ field, form }) => (
                    <FormControl
                      isInvalid={
                        form.errors.offerDescription?.fr &&
                        form.touched.offerDescription?.fr
                      }
                    >
                      <FormLabel fontSize="sm">
                        {translate(
                          "businessForm.coupon.offerDescription.french.label"
                        )}
                      </FormLabel>
                      <Textarea
                        {...field}
                        placeholder={translate(
                          "businessForm.coupon.offerDescription.french.placeholder"
                        )}
                      />
                      <FormErrorMessage>
                        {form.errors.offerDescription?.fr}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                </Field>
              )}
            </Stack>

            <Field name="maxValue">
              {({ field, form }) => (
                <FormControl
                  isInvalid={form.errors.maxValue && form.touched.maxValue}
                >
                  <FormLabel fontSize="sm">
                    {translate("businessForm.coupon.maxValue.label")}
                  </FormLabel>
                  <Input
                    {...field}
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder={translate(
                      "businessForm.coupon.maxValue.placeholder"
                    )}
                    onWheel={(e) => e.currentTarget.blur()}
                  />
                  <FormErrorMessage>{form.errors.maxValue}</FormErrorMessage>
                </FormControl>
              )}
            </Field>

            <Stack
              direction={{ base: "column", md: "row" }}
              spacing={6}
              width="100%"
            >
              <Field name="termsAndExclusions.en">
                {({ field, form }) => (
                  <FormControl
                    isInvalid={
                      form.errors.termsAndExclusions?.en &&
                      form.touched.termsAndExclusions?.en
                    }
                  >
                    <FormLabel fontSize="sm">
                      {translate(
                        showFrench
                          ? "businessForm.coupon.termsAndExclusions.english.label"
                          : "businessForm.coupon.termsAndExclusions.label"
                      )}
                    </FormLabel>
                    <Textarea
                      {...field}
                      placeholder={translate(
                        "businessForm.coupon.termsAndExclusions.english.placeholder"
                      )}
                    />
                    <FormErrorMessage>
                      {form.errors.termsAndExclusions?.en}
                    </FormErrorMessage>
                  </FormControl>
                )}
              </Field>
              {showFrench && (
                <Field name="termsAndExclusions.fr">
                  {({ field, form }) => (
                    <FormControl
                      isInvalid={
                        form.errors.termsAndExclusions?.fr &&
                        form.touched.termsAndExclusions?.fr
                      }
                    >
                      <FormLabel fontSize="sm">
                        {translate(
                          "businessForm.coupon.termsAndExclusions.french.label"
                        )}
                      </FormLabel>
                      <Textarea
                        {...field}
                        placeholder={translate(
                          "businessForm.coupon.termsAndExclusions.french.placeholder"
                        )}
                      />
                      <FormErrorMessage>
                        {form.errors.termsAndExclusions?.fr}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                </Field>
              )}
            </Stack>

            <FormControl>
              <FormLabel fontSize="sm">
                {translate("businessForm.businessInfo.logo.label")}
              </FormLabel>
              <Input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={onFilePicked}
                display="none"
                id="logo-upload"
              />
              <Flex
                direction={{ base: "column", md: "row" }}
                align={{ base: "stretch", md: "flex-start" }}
                gap={4}
              >
                <VStack
                  spacing={2}
                  align="stretch"
                  w="100%"
                  maxW="360px"
                  flexShrink={0}
                >
                  {resolvedLogoImage ? (
                    <Image
                      src={resolvedLogoImage}
                      alt="Logo preview"
                      w="100%"
                      h="175px"
                      objectFit="cover"
                      borderRadius="md"
                    />
                  ) : (
                    <Flex
                      w="100%"
                      h="175px"
                      bg="gray.100"
                      borderRadius="md"
                      justifyContent="center"
                      alignItems="center"
                    >
                      <FaStore size={fallbackIconSize} color="gray.400" />
                    </Flex>
                  )}
                  {logoPreview && canRecropExistingPreview && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={reopenCropperFromPreview}
                    >
                      Re-crop image
                    </Button>
                  )}
                  <Button
                    as="label"
                    htmlFor="logo-upload"
                    cursor="pointer"
                    variant="outline"
                    width="100%"
                  >
                    {translate("businessForm.businessInfo.logo.uploadButton")}
                  </Button>
                  <Button
                    leftIcon={<SearchIcon />}
                    variant="outline"
                    onClick={openPexelsPicker}
                    width="100%"
                  >
                    Choose from Pexels
                  </Button>
                </VStack>
                <Box
                  flex="1"
                  p={3}
                  bg="gray.50"
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor="gray.200"
                  textAlign="left"
                >
                  <Text fontSize="sm" fontWeight="medium" mb={1}>
                    Image tips
                  </Text>
                  <UnorderedList fontSize="xs" color="gray.600" spacing={0.5}>
                    <ListItem>
                      Recommended size: 720 &times; 350 px (a 2:1 landscape
                      image)
                    </ListItem>
                    <ListItem>JPG or PNG, under 5 MB ideally</ListItem>
                    <ListItem>
                      Keep the important part centered &mdash; the edges may be
                      cropped on small screens
                    </ListItem>
                    <ListItem>
                      Leave blank to use a default image based on your category
                    </ListItem>
                  </UnorderedList>
                </Box>
              </Flex>
            </FormControl>
          </VStack>
        );

      case 3: // Preview
        return <PreviewStep />;

      default:
        return null;
    }
  };

  return (
    <Form>
      <LogoCropperModal
        isOpen={isCropperOpen}
        sourceUrl={cropSourceUrl}
        onCancel={onCropCancel}
        onApply={onCropApplied}
      />
      <PexelsSearchModal
        isOpen={isPexelsOpen}
        onClose={() => setIsPexelsOpen(false)}
        onPick={handlePexelsPick}
        defaultQuery={values.category?.en || ""}
      />
      <VStack spacing={{ base: 6, md: 8 }} width="100%">
        {singlePage ? (
          <VStack
            spacing={{ base: 8, md: 10 }}
            width="100%"
            align="stretch"
          >
            {steps.map((step, idx) => (
              <Box key={idx} width="100%">
                <Heading
                  as="h2"
                  size="md"
                  color="heading"
                  pb={{ base: 3, md: 4 }}
                  borderBottomWidth="1px"
                  borderColor="gray.200"
                  mb={{ base: 4, md: 6 }}
                >
                  {translate(step.title)}
                </Heading>
                {renderStepContent(idx)}
              </Box>
            ))}
          </VStack>
        ) : (
          <>
            <FormStepper steps={steps} activeStep={activeStep} />
            <Box width="100%" pt={{ base: 4, md: 10 }}>
              {renderStepContent(activeStep)}
            </Box>
          </>
        )}
        <HStack
          justify={singlePage ? "flex-end" : "space-between"}
          width="100%"
          pt={{ base: 4, md: 6 }}
          flexDir={{ base: "column", sm: "row" }}
          spacing={{ base: 4, sm: 0 }}
        >
          {!singlePage && (
            <Button
              variant="ghost"
              onClick={() => setActiveStep(Math.max(activeStep - 1, 0))}
              isDisabled={activeStep === 0}
              width={{ base: "100%", sm: "auto" }}
            >
              {translate("businessForm.navigation.previous")}
            </Button>
          )}
          <Button
            variant="secondary"
            type="submit"
            isLoading={isSubmitting}
            width={{ base: "100%", sm: "auto" }}
          >
            {singlePage || activeStep === steps.length - 1
              ? translate("businessForm.navigation.submit")
              : translate("businessForm.navigation.next")}
          </Button>
        </HStack>
      </VStack>
    </Form>
  );
};

export default BusinessFormContent;
